import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { UserProfile } from "./types";
import { subscribeToAuth } from "./services/auth";
import { subscribeToAnswers, calculateFreshness } from "./services/answersService";
import { subscribeToUserSmeTasks } from "./services/smeTasksService";
import { ProtectedRoute } from "./components/layout/ProtectedRoute";
import { AppLayout } from "./components/layout/AppLayout";
import { PipelineView } from "./components/views/PipelineView";
import { LibraryView } from "./components/views/LibraryView";
import { ReviewQueueView } from "./components/views/ReviewQueueView";
import { SettingsView } from "./components/views/SettingsView";
import { StaffSettingsView } from "./components/views/StaffSettingsView";
import { LoginView } from "./components/views/LoginView";
import { OpportunityWorkspaceView } from "./components/views/OpportunityWorkspaceView";
import { WorkspacePickerView } from "./components/views/WorkspacePickerView";
import { PlatformGuideView } from "./components/views/PlatformGuideView";
import { getActiveOpportunity } from "./services/activeOpportunityService";

const WorkspaceRouteHandler: React.FC<{ user: UserProfile | null }> = ({ user }) => {
  const active = getActiveOpportunity();
  if (active && active.id) {
    return <Navigate to={`/opportunities/${active.id}`} replace />;
  }
  return <WorkspacePickerView user={user} />;
};

export const App: React.FC = () => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingSmeCount, setPendingSmeCount] = useState(0);
  const [overdueAnswersCount, setOverdueAnswersCount] = useState(0);

  useEffect(() => {
    const unsubscribe = subscribeToAuth((currentUser, isLoading) => {
      setUser(currentUser);
      setLoading(isLoading);
    });

    return () => unsubscribe();
  }, []);

  // Subscribe to user SME tasks for review badge
  useEffect(() => {
    const userEmail = user?.email;
    if (!userEmail) {
      return;
    }

    const unsubscribe = subscribeToUserSmeTasks(
      userEmail,
      (tasks) => {
        const pending = tasks.filter((t) => t.status === "open" || t.status === "returned").length;
        setPendingSmeCount(pending);
      },
      (err) => console.warn("Failed to subscribe to SME tasks in App:", err),
    );

    return () => {
      unsubscribe();
      setPendingSmeCount(0);
    };
  }, [user?.email]);

  // Subscribe to answers for overdue count
  useEffect(() => {
    if (!user) {
      return;
    }

    const unsubscribe = subscribeToAnswers(
      (answers) => {
        const overdue = answers.filter((a) => {
          if (a.status === "retired") return false;
          return calculateFreshness(a.reviewDueDate, a.status).isOverdue;
        }).length;
        setOverdueAnswersCount(overdue);
      },
      (err) => console.warn("Failed to subscribe to answers in App:", err),
    );

    return () => {
      unsubscribe();
      setOverdueAnswersCount(0);
    };
  }, [user]);

  const effectivePendingSme = user?.email ? pendingSmeCount : 0;
  const effectiveOverdue = user ? overdueAnswersCount : 0;
  const reviewBadgeCount = effectivePendingSme + effectiveOverdue;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginView />} />

        <Route
          path="/*"
          element={
            <ProtectedRoute user={user} loading={loading}>
              <AppLayout user={user} reviewBadgeCount={reviewBadgeCount}>
                <Routes>
                  <Route path="/" element={<PipelineView user={user} />} />
                  <Route path="/workspace" element={<WorkspaceRouteHandler user={user} />} />
                  <Route
                    path="/opportunities/:id"
                    element={<OpportunityWorkspaceView user={user} />}
                  />
                  <Route path="/library" element={<LibraryView user={user} />} />
                  <Route path="/reviews" element={<ReviewQueueView user={user} />} />
                  <Route path="/settings" element={<SettingsView />} />
                  <Route path="/settings/staff" element={<StaffSettingsView user={user} />} />
                  <Route path="/guide" element={<PlatformGuideView />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AppLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
